import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useWorkflows } from '../../client-workflows/useData';
import { useTheme } from '../../../contexts/ThemeContext';

export interface ActiveWorkflowsWidgetProps {
  embedded?: boolean;
  settings?: Record<string, unknown>;
  isEditing?: boolean;
}

const ActiveWorkflowsWidget: React.FC<ActiveWorkflowsWidgetProps> = ({
  embedded,
}) => {
  const { data: workflows, isLoading } = useWorkflows(false);
  const { colors } = useTheme();
  const navigate = useNavigate();

  const active = Array.isArray(workflows)
    ? workflows.filter((w: { isActive?: boolean }) => w.isActive)
    : [];

  const cardClass = embedded
    ? 'min-w-0'
    : `${colors.surface} rounded-xl shadow-sm p-5 ${colors.border} border`;

  if (isLoading) {
    return (
      <div className={cardClass}>
        <h2 className={`text-base font-semibold ${colors.text} mb-3`}>
          Active Workflows
        </h2>
        <div className="animate-pulse h-16 rounded-lg bg-gray-100 dark:bg-gray-700" />
      </div>
    );
  }

  return (
    <div className={cardClass}>
      <div className="flex items-center justify-between mb-3 flex-shrink-0">
        <h2 className={`text-base font-semibold ${colors.text}`}>
          Active Workflows
        </h2>
        <button
          type="button"
          onClick={() => navigate('/workflows')}
          className="text-sm text-teal-600 hover:text-teal-700 dark:text-teal-400 font-medium"
        >
          View All
        </button>
      </div>
      <p className={`text-2xl font-bold ${colors.text}`}>{active.length}</p>
      <p className={`text-xs ${colors.textMuted} mt-1`}>
        {active.length === 1 ? 'workflow' : 'workflows'} active
      </p>
      {active.length > 0 && (
        <ul className="mt-3 space-y-1">
          {active.slice(0, 3).map((w: { id: number; name?: string }) => (
            <li
              key={w.id}
              role="button"
              tabIndex={0}
              onClick={() => navigate(`/workflows`)}
              onKeyDown={(e) => e.key === 'Enter' && navigate(`/workflows`)}
              className={`text-sm truncate cursor-pointer hover:underline ${colors.text}`}
            >
              {w.name || `Workflow #${w.id}`}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default ActiveWorkflowsWidget;
